import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, OnInit, Signal, signal, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { map, take, tap } from 'rxjs/operators';
import { FileItem, FileLikeObject } from 'ng2-file-upload';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IrisDmsFileItemI } from '@iris/common/modules/dms/models/IrisDmsFileItem';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { FileUploadState, IrisDMSFileI } from '../../../models/IrisDMSFile';
import { IrisDmsFileUploadHelper } from '@iris/common/modules/dms/helpers/dms-file-upload.helper';
import { IrisTagI } from '@iris/common/models/IrisTag';
import { FileVersionByNameI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { isSubarray } from '@iris/common/utils/array.utils';
import { IrisUserService } from '@iris/common/services/user.service';
import { extendUrl } from '@iris/common/utils/url.utils';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { isViewportSmall } from '@iris/common/utils/dom.utils';
import { BulkFileUploader } from '@iris/common/modules/file-uploader/bulk-file-uploader';
import { v4 as uuid } from 'uuid';
import { DmsWebsocketService } from '@iris/common/services/websockets/dms-websocket.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisTimePipe } from '@iris/common/modules/time';
import {
  StrakitButtonComponent,
  StrakitCheckboxComponent,
  StrakitSelectComponent,
  StrakitToggleComponent,
} from '@ngx-strakit/cdk/controls';
import { FormsModule } from '@angular/forms';
import { UpperCasePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { IrisFileSizePipe } from '@iris/common/modules/pipes-common/pipes';
import { IrisDmsSelectTagsButtonComponent } from '@iris/modules/dms/components/file-actions/dms-select-tags-button/dms-select-tags-button.component';
import { IrisDmsFileRenameModalComponent } from '../file-rename-modal/file-rename-modal.component';
import { UploadFilesModalParamsI } from '../../../sandbox/dms-modals.sandbox';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';
import { StrakitCellComponent } from '@ngx-strakit/cdk/layout';

@Component({
  selector: 'iris-upload-files-modal',
  imports: [
    FormsModule,
    TranslatePipe,
    UpperCasePipe,

    MatTooltipModule,
    MatInputModule,
    ConfirmByKeypressDirective,

    SkModalContentComponent,
    StrakitToggleComponent,
    StrakitSelectComponent,
    StrakitCheckboxComponent,

    IrisDmsSelectTagsButtonComponent,

    IrisFileSizePipe,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
    StrakitCellComponent,
  ],
  templateUrl: './upload-files-modal.component.html',
  styleUrls: ['./upload-files-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    IrisDmsFileUploadHelper,
    IrisFilesService,
  ],
})
export class IrisUploadFilesModalComponent extends SkModalBase<IrisUploadFilesModalComponent, IrisDMSFileI[]> implements OnInit {
  private readonly filesService = inject(IrisFilesService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsFileUploadHelper = inject(IrisDmsFileUploadHelper);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly userService = inject(IrisUserService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly timePipe = inject(IrisTimePipe);
  private readonly dialog = inject(MatDialog);
  private readonly dmsWebsocketService = inject(DmsWebsocketService);

  readonly modalParams = signal<UploadFilesModalParamsI>(inject<UploadFilesModalParamsI>(MAT_DIALOG_DATA));
  readonly folderId = computed(() => this.modalParams()?.folderId || null);
  readonly fileId = computed(() => this.modalParams()?.fileId || null);
  readonly fileName = computed(() => this.modalParams()?.fileName || null);
  readonly isUploadVersion = computed(() => this.modalParams()?.isUploadVersion || false);
  readonly headerLabel = computed(() => this.isUploadVersion() ? 'label.dms.UploadNewVersion' : 'label.dms.UploadFiles');

  readonly uploader = signal<BulkFileUploader>(this.modalParams()?.uploader);
  readonly queue = computed<IrisDmsFileItemI[]>(() => (this.uploader()?.queue as IrisDmsFileItemI[]) ?? []);
  readonly notUploadedItems = computed<IrisDmsFileItemI[]>(() => this.uploader()?.getNotUploadedItems() as IrisDmsFileItemI[] ?? []);
  readonly hasZipFile = computed(() => this.queue().some(({ file }) => this.filesService.isZipFile(file.name)));
  readonly isHTML5 = computed(() => this.uploader()?.options.isHTML5);

  @ViewChild('fileInput') fileInput: ElementRef;

  readonly hasErrors = signal(false);
  readonly hasCancels = signal(false);
  readonly uploadedFilesCount = signal(0);

  readonly uploadedFiles: IrisDMSFileI[] = [];
  readonly filesInQueueCount = signal(1);
  isEnableAdvancedUploadingSettings = false;
  readonly newVersionsNumbers = signal<FileVersionByNameI>({});
  readonly errorVersionText = signal('');
  readonly sameFileType = signal(true);

  readonly isUploadDisabled = computed(() => {
    return !this.notUploadedItems().length && !this.hasErrors()
      || this.isUploadingProcess
      || !this.sameFileType()
      || this.queue().every(fileItem => this.isFileOversized(fileItem.file))
      || this.renameDialogOpened;
  });

  readonly languages = toSignal(this.globalSandbox.languages$);

  readonly maxFileSizeText = this.dmsFileUploadHelper.maxFileSizeText;

  private renameDialogOpened = false;

  readonly timeZone = toSignal(this.globalSandbox.currentProject$.pipe(
    map(project => project?.timeZone),
  ));

  get currentUser(): IdentUserI {
    return this.userService.me;
  }

  maxFileSizeWarning(file: FileLikeObject): string {
    return this.dmsFileUploadHelper.getMaxFileSizeWarning(file.name);
  }

  get hideForSmallScreen(): boolean {
    return isViewportSmall();
  }

  get isUploadingProcess(): boolean {
    return (this.uploader()?.queue ?? []).some(item => item.isUploading);
  }

  private _scrollBodyToTop(): void {
    const modalBody = this.elementRef.nativeElement.querySelector('.modal-body');
    modalBody.scrollTop = 0;
  }

  private _setFilesInQueueVersions(): void {
    const namesInQueue = this.queue().map(({ file }) => file.name);
    const shouldSkip = isSubarray(Object.keys(this.newVersionsNumbers()), namesInQueue, { discardEmpty: true });
    if (shouldSkip) { return; }

    this.dmsFilesSandbox.getFilesNextVersionsByNames(namesInQueue, this.folderId())
      .pipe(
        take(1),
        tap((newVersions) => this.newVersionsNumbers.set({ ...newVersions })),
      )
      .subscribe();

    if (this.isUploadVersion() && !!this.fileName() && !!namesInQueue.length) {
      this.dmsFilesSandbox.checkSameFileType(this.fileName(), namesInQueue[0])
        .pipe(
          take(1),
          tap(error => {
            this.errorVersionText.set(error);
            this.sameFileType.set(!error);
          }),
        )
        .subscribe();
    }
  }

  private _processPreselectedFiles(): void {
    this.queue().forEach(file => this._setFileDefaultValues(file));
  }

  private _setFileDefaultValues(file: IrisDmsFileItemI): void {
    file.contentLanguages = [];
    file.file = this.renameEmailFile(file.file, this.timeZone());
  }

  ngOnInit(): void {
    this.setSelectedFolder();
    this.setFileUploader();
  }

  private getTagsIds(tags: IrisTagI[]): number[] {
    return (tags || []).map(({ id }) => id);
  }

  private setSelectedFolder(): void {
    if (!this.folderId()) {
      return;
    }
    this.dmsFoldersSandbox.getFolder(this.folderId());
    this.dmsFoldersSandbox.setSelectedFolderModal(this.folderId());
  }

  private setFileUploader(): void {
    if (!this.uploader()) {
      this.uploader.set(this.dmsFileUploadHelper.fileUploader);
    }

    const uploader = this.uploader();

    if (uploader.queue.length) {
      this._setFilesInQueueVersions();
      this._processPreselectedFiles();
    }

    uploader.setOptions({
      url: this.filesService.getUploadUrl(this.folderId()),
    });

    uploader.onAfterAddingFile = (item: IrisDmsFileItemI): void => this._setFileDefaultValues(item);

    uploader.onSuccessItem = (_file: IrisDmsFileItemI, response: string): void => {
      this.uploadedFilesCount.set(this.uploadedFilesCount() + 1);
      if (this.uploadedFilesCount() === this.filesInQueueCount()) {
        if (!this.hasErrors() && !this.hasCancels()) {
          this.toastService.success({ message: this.translateService.instant('text.dms.FilesUploadedSuccess') });
          this.closeWithResult(this.uploadedFiles);
        }
        if (!this.isUploadVersion()) {
          this.dmsFilesSandbox.reloadCurrentPage();
        }
        uploader.clearQueue();
      }
      let parsedResponse: IrisDMSFileI[];
      try {
        parsedResponse = (JSON.parse(response) || []) as IrisDMSFileI[];
      } catch {
        parsedResponse = [];
      }
      this.dmsFilesSandbox.updateUploadingFileState(_file.file.name, FileUploadState.Completed);
      if (parsedResponse?.length) {
        this.uploadedFiles.push(...parsedResponse);
        if (_file.unzipFile) {
          this.dmsFilesSandbox.addUploadingFileFolder(_file.file.name, parsedResponse[0].parentId);
        }
      }
    };

    uploader.onErrorItem = (fileItem: FileItem, response: string): void => {
      const errorMessage = (JSON.parse(response) as { messages: string[] })?.messages?.[0];
      this.toastService.error({ message: errorMessage });
      this.dmsFilesSandbox.updateUploadingFileState(fileItem.file.name, FileUploadState.Error);
      this.hasErrors.set(true);
      fileItem.isUploaded = false;
    };

    uploader.onCancelItem = (fileItem: FileItem): void => {
      const fileName = fileItem?.file?.name;
      this.dmsFilesSandbox.updateUploadingFileState(fileName, FileUploadState.Canceled);
      this.toastService.warning({ message: this.translateService.instant('text.dms.FilesUploadCanceled', { fileName }) });
      this.hasCancels.set(true);
    };

    uploader.onProgressItem = (): void => this.changeDetector.markForCheck();

    this.newVersionsNumbers.set({});

    this.uploader.set(uploader);
  }

  private refreshAllUrls(): void {
    this.queue()
      .filter(fileItem => !this.isFileOversized(fileItem.file))
      .forEach((file: IrisDmsFileItemI) => {
        const tagsIds = [...this.getTagsIds(file.tags)];
        file.url = this.refreshUrl(file, tagsIds);
      });
  }

  private refreshUrl(file: IrisDmsFileItemI, tagsIds: number[] = []): string {
    let url = file.unzipFile ?  this.filesService.getZipImportUrl(this.folderId()) : this.filesService.getUploadUrl(this.folderId());

    if (file?.contentLanguages?.length) {
      const languages = (file.contentLanguages || []).map(language => `'${language}'`).toString();
      url = extendUrl(url, `contentLanguage=[${languages}]`);
    }

    tagsIds = tagsIds || this.getTagsIds(file.tags);

    if (tagsIds.length) {
      url = extendUrl(url, `tags=${JSON.stringify(tagsIds)}`);
    }

    return url;
  }

  public uploadAll(): void {
    if (!this.queue()?.length || this.isUploadDisabled()) {
      return;
    }
    this.hasErrors.set(false);
    this.hasCancels.set(false);
    this._scrollBodyToTop();

    if (this.isUploadVersion()) {
      this.queue().forEach((file: IrisDmsFileItemI) => {
        file.url = this.filesService.getUploadVersionUrl(this.fileId(), this.currentUser.id);

        if (this.fileName() !== file.file.name) {
          file.url = extendUrl(file.url, 'change-name=true');
        }

        this.dmsFilesSandbox.addUploadingFiles([file]);

        this.uploader().uploadItem(file);
      });
    } else {
      this.refreshAllUrls();
      const filesToUnzip = this.notUploadedItems().filter(fileItem => fileItem.unzipFile);

      filesToUnzip.forEach((fileItem: IrisDmsFileItemI) => {
        fileItem.upload();
      });

      const notUploadedFiles = this.notUploadedItems().filter(fileItem => !fileItem.unzipFile && !this.isFileOversized(fileItem.file));

      this.filesInQueueCount.set(notUploadedFiles.length + filesToUnzip.length);
      this.dmsFilesSandbox.addUploadingFiles(notUploadedFiles);
      const uploadId = uuid();

      const filesUploadInfo = notUploadedFiles.map((file) => {
        return {
          fileName: file.file.name, tags: file.tags?.map(tag => tag.id),
          contentLanguage: `[${(file.contentLanguages || []).map(language => `'${language}'`).toString()}]`,
        };
      });
      this.filesService.saveFilesInfo(uploadId, filesUploadInfo).pipe(
        tap(() => {
          if (notUploadedFiles.length) {
            this.uploader().setOptions({
              url: extendUrl(this.filesService.getUploadUrl(this.folderId()), `uploadId=${uploadId}`),
            });
            notUploadedFiles.map(file => file.uploadId = uploadId);
            this.uploader().uploadAllBulk();
            this.dmsWebsocketService.subscribeToUpload(uploadId);
          }
        }),
      ).subscribe();
    }

    this.uploadedFilesCount.set(0);
  }

  public doesNameExist(fileName: string): Signal<boolean> {
    return computed(() => this.newVersionsNumbers()?.[fileName] !== 1);
  }

  public getVersionLabel(item: FileItem): Signal<string> {
    return computed(() => {
      const newVersionNumber = this.newVersionsNumbers()[item.file.name];
      if (!newVersionNumber) { return null; }
      return newVersionNumber === 1
        ? `${this.translateService.instant('label.Version')} ${newVersionNumber}`
        : `${this.translateService.instant('label.dms.UploadAsVersion', { version: newVersionNumber })}`;
    });
  }

  public getIconTooltip(file: FileItem): string {
    if (file.isCancel) {
      return 'text.dms.CancelUploadingFile';
    }
    if (file.isError) {
      return 'text.dms.ErrorUploadingFile';
    }
    if (file.isSuccess) {
      return 'text.dms.SuccessUploadingFile';
    }
  }

  public showFileRenameDialog(file: FileItem): void {
    const fileName = this.filesService.getFileNameWithoutExtension(file.file.name);
    const fileExtension = this.filesService.getExtensionFromFileName(file.file.name);
    const dialogRef: MatDialogRef<IrisDmsFileRenameModalComponent> = this.dialog
      .open(IrisDmsFileRenameModalComponent, {
        data: { fileName, folderId: this.folderId() },
      });

    this.renameDialogOpened = true;

    dialogRef.componentInstance.result$.pipe(
      take(1),
      tap(({ name, rename }) => {
        if (rename && !!name && name !== fileName) {
          file.file.name = !fileExtension ? name : `${name}.${fileExtension}`;
          this._setFilesInQueueVersions();
        }
      }),
    ).subscribe();

    dialogRef.componentInstance.close$
      .pipe(
        take(1),
        tap(() => (this.renameDialogOpened = false)),
      ).subscribe();
  }

  public removeFile(file: FileItem): void {
    file.remove();
    if (!this.queue().length) {
      this.closeWithoutResult();
    }
  }

  public isFileOversized(file: File | FileLikeObject): boolean {
    return this.dmsFileUploadHelper.isFileOversized(file);
  }

  public isZipFile(file: FileItem): boolean {
    return this.filesService.isZipFile(file.file.name);
  }

  public cancelAll(): void {
    this.uploader().onCancelItem = (fileItem: FileItem): void => {
      const fileName = fileItem?.file?.name;
      this.dmsFilesSandbox.updateUploadingFileState(fileName, FileUploadState.Canceled);
    };
    this.uploader().cancelAll();
    this.toastService.warning({ message: this.translateService.instant('text.dms.AllUploadsWereCanceled') });
  }

  private renameEmailFile(file: FileLikeObject, timeZone: string): FileLikeObject {
    const namePrefix = this.timePipe.transform(new Date(), 'YYYY-MM-DD_HHmmss', timeZone);
    if (this.filesService.isEmailFile(file.name) && !file.name.startsWith(namePrefix)) {
      file.name = `${namePrefix}_${file.name}`;
    }
    return file;
  }
}
